import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Input, Modal } from 'antd';
import User from '../../../models/User';
import TalentCandidate from '../../../models/TalentCandidate';
import { useAuth } from '../../../providers/AuthProvider';
import Form, { FormInputComponent } from '../../../components/form/Form';

/**
 * "View feedback" button + modal for a profile Wripple didn't approve:
 * shows the feedback and lets the talent submit an appeal.
 * (Moved out of ApplicationSideNavBar so the profile status bar can use it.)
 */
const ProfileAppealModal = ({ isAppeal, talentUser, buttonClassName = 'btn btn-primary btn-block' }) => {
    const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
    const [showAppealForm, setShowAppealForm] = useState(false);

    const { update: updateAuth, isUpdating } = useAuth();

    const handleClose = () => {
        setIsAppealModalOpen(false);
        setShowAppealForm(false);
    }

    const addTalentProfileAppealMutation = useMutation(['applicationProfileAppeal'],
        params => TalentCandidate.addTalentProfileAppeal(params),
        {
            onSuccess: async data => {
                handleClose();

                const updatedUser = User.make({ ...talentUser, latest_appeal: data.appeal });
                const didUpdate = await updateAuth(updatedUser);

                if (didUpdate) {
                    TB.alert('success', 'Profile Appeal Submitted', 'A talent manager will review your appeal and will reach out via email.')
                }
            },
            onError: err => {
                console.log(err, 'Error addTalentProfileAppealMutation');
            }
        }
    )

    return (
        <div className="flex flex-col items-stretch">
            <button
                type="button"
                className={buttonClassName}
                disabled={isUpdating}
                onClick={() => setIsAppealModalOpen(true)}
            >
                View Feedback
            </button>

            <Modal
                title={`${isAppeal ? 'Appeal' : 'Profile'} Not Approved`}
                closable={true}
                maskClosable={true}
                style={{ top: 150 }}
                destroyOnClose={true}
                width={500}
                footer={null}
                open={isAppealModalOpen}
                onCancel={handleClose}
                onClose={handleClose}
            >

                {showAppealForm ? (
                    <Form
                        showRequired
                        background={'transparent'}
                        title={null}
                        subtitle={null}
                        submitButton={false}
                        className="[&>.form-container]:p-0"
                        style={{ padding: 20 }}
                        onSubmit={async fields => addTalentProfileAppealMutation.mutateAsync(fields)}
                        isDisabled={addTalentProfileAppealMutation.isLoading}
                        fields={[]}
                    >
                        <div>
                            <label htmlFor="appeal-reason" className="block pb-1">
                                Write a note to explain your reason for your profile appeal. After your appeal is
                                submitted, a Wripple Talent Manager will review and reach out via email if there are any
                                questions.
                            </label>

                            <FormInputComponent
                                fieldName="notes"
                                disabled={addTalentProfileAppealMutation.isLoading}
                                required
                                label="Notes"
                                type="antd-input"
                            >
                                <Input.TextArea
                                    showCount
                                    bordered
                                    id="appeal-reason"
                                    autoSize={{ minRows: 4 }}
                                    maxLength={500}
                                />
                            </FormInputComponent>
                        </div>
                        <div className="flex flex-row justify-between items-center gap-3">
                            <div className="mr-auto">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => setShowAppealForm(false)}
                                >
                                    Back
                                </button>

                                {addTalentProfileAppealMutation.failureReason && (
                                    <p className="text-danger mr-auto">{addTalentProfileAppealMutation.failureReason?.message}</p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-dark"
                                disabled={addTalentProfileAppealMutation.isLoading}
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={addTalentProfileAppealMutation.isLoading}
                            >
                                Submit
                            </button>
                        </div>
                    </Form>
                ) : (
                    <>
                        <div>
                            <p>
                                Your {isAppeal ? 'appeal' : 'profile'} is not approved at this time.
                                Further feedback from the Wripple Talent Team has been provided below.
                                {!isAppeal ? ' If you believe this decision is incorrect, you may request an appeal at any time using the button below.' : ''}
                            </p>

                            <Alert
                                className="mt-4"
                                description={(
                                    <div>
                                        <p className="text-xs text-gray-500">
                                            Wripple Feedback:
                                        </p>

                                        <p className="pt-2">
                                            {talentUser.latest_appeal?.rejection_notes || talentUser.talent_profile_rejection_notes}
                                        </p>
                                    </div>
                                )}
                                type="warning"
                            />

                            {talentUser.latest_appeal && (
                                <div className="pt-4">
                                    <p>Your latest appeal{talentUser.latest_appeal.is_rejected ? '' : ' (pending review)'}:</p>
                                    <Alert
                                        className="mt-2.5"
                                        description={(
                                            <div>
                                                <p className="text-xs text-gray-500">
                                                    Your Appeal:
                                                </p>

                                                <p className="pt-2">
                                                    {talentUser.latest_appeal.notes}
                                                </p>
                                            </div>
                                        )}
                                        type="info"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="pt-5 flex flex-row justify-between gap-3">
                            {/* {(talentUser.latest_appeal.is_approved || talentUser.latest_appeal.is_rejected) && ( */}
                            <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => {
                                    if (talentUser.latest_appeal?.is_approved === false && talentUser.latest_appeal?.is_rejected === false) {
                                        handleClose();
                                        return TB.alert('warning', "Can't resubmit appeal at this time", 'Your current appeal is pending review.')
                                    }
                                    // if (talentUser.can_submit_appeal) {
                                    setShowAppealForm(true)
                                    // } else {
                                    //     handleClose();
                                    //     TB.alert('warning', "Can't resubmit appeal at this time", 'Your last appeal was not approved. You can resubmit an appeal 1 year from the date it was not approved if you still disagree with this decision.')
                                    // }
                                }}
                            >
                                Submit Appeal
                            </button>
                            {/* )} */}

                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleClose}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    )
}

export default ProfileAppealModal;
